import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_SHOW_REALTIME, DEFAULT_TIEBREAKER, WINNERS_COUNT } from '../constants';
import {
  ParticipationDTO,
  PronoDrawDTO,
  PronoGameInDTO,
  PronoGameListItemDTO,
  PronoGameOutDTO,
  PronoStatsResponseDTO,
  PronoStatus,
  PronosticMode,
  SportType,
  WinnerRule,
} from '../types';
import { pronoGamesAdapter } from './prono.adapter';
import {
  createPronoGameThunk,
  deletePronoThunk,
  fetchPronoGamesThunk,
  fetchPronoParticipantsThunk,
  getPronoDrawThunk,
  getPronoInfoThunk,
  getPronoStatsThunk,
  runPronoDrawThunk,
  setPronoParticipationThunk,
  updatePronoGameThunk,
} from './prono.thunk';

export type PronoForm = Partial<PronoGameOutDTO>;

interface PronoList extends EntityState<PronoGameListItemDTO> {
  isLoading: boolean;
  hasError: boolean;
  total: number;
}

export interface PronoState {
  list: PronoList;
  current: {
    isLoading: boolean;
    hasBeenSaved: boolean;
    hasError: boolean;
    form: PronoForm;
    isStepValid: boolean;
  };
  selected: {
    gameInfo: PronoGameInDTO | undefined;
    participants: ParticipationDTO[];
    totalParticipants: number;
    stats: PronoStatsResponseDTO | undefined;
    draw: PronoDrawDTO | undefined;
    isLoading: boolean;
    hasError: boolean;
    isParticipantsLoading: boolean;
    isDrawing: boolean;
  };
}

export const defaultPronoForm: PronoForm = {
  storeIds: [],
  name: '',
  sport: SportType.FOOTBALL,
  team1: '',
  team2: '',
  pronosticMode: PronosticMode.SCORE_ONLY,
  lot: { name: '' },
  winnerRule: WinnerRule.EXACT,
  tiebreaker: DEFAULT_TIEBREAKER,
  winnersCount: WINNERS_COUNT.DEFAULT,
  allowMultipleParticipations: false,
  showRealtime: DEFAULT_SHOW_REALTIME,
  smsWinnerEnabled: false,
};

const initialState: PronoState = {
  list: pronoGamesAdapter.getInitialState({ isLoading: false, hasError: false, total: 0 }),
  current: {
    isLoading: false,
    hasBeenSaved: false,
    hasError: false,
    form: defaultPronoForm,
    isStepValid: false,
  },
  selected: {
    gameInfo: undefined,
    participants: [],
    totalParticipants: 0,
    stats: undefined,
    draw: undefined,
    isLoading: false,
    hasError: false,
    isParticipantsLoading: false,
    isDrawing: false,
  },
};

export const { actions, reducer } = createSlice({
  name: 'prono',
  initialState,
  reducers: {
    updateCurrentProno: (state, action: PayloadAction<PronoForm>) => {
      state.current.form = { ...state.current.form, ...action.payload };
    },
    resetCurrentForm: state => {
      state.current.form = defaultPronoForm;
      state.current.isStepValid = false;
      state.current.hasBeenSaved = false;
    },
    setValidForm: (state, action: PayloadAction<boolean>) => {
      state.current.isStepValid = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchPronoGamesThunk.pending, state => {
      state.list.isLoading = true;
      state.list.hasError = false;
    });
    builder.addCase(fetchPronoGamesThunk.fulfilled, (state, action) => {
      pronoGamesAdapter.setAll(state.list, action.payload.games);
      state.list.total = action.payload.total;
      state.list.isLoading = false;
      state.list.hasError = false;
    });
    builder.addCase(fetchPronoGamesThunk.rejected, state => {
      pronoGamesAdapter.removeAll(state.list);
      state.list.total = 0;
      state.list.isLoading = false;
      state.list.hasError = true;
    });

    builder.addCase(createPronoGameThunk.pending, state => {
      state.current.isLoading = true;
      state.current.hasError = false;
    });
    builder.addCase(createPronoGameThunk.fulfilled, state => {
      state.current.isLoading = false;
      state.current.hasBeenSaved = true;
      state.current.form = defaultPronoForm;
    });
    builder.addCase(createPronoGameThunk.rejected, state => {
      state.current.isLoading = false;
      state.current.hasError = true;
    });

    builder.addCase(getPronoInfoThunk.pending, state => {
      state.selected.isLoading = true;
      state.selected.hasError = false;
    });
    builder.addCase(getPronoInfoThunk.fulfilled, (state, action) => {
      state.selected.gameInfo = action.payload;
      state.selected.isLoading = false;
    });
    builder.addCase(getPronoInfoThunk.rejected, state => {
      state.selected.isLoading = false;
      state.selected.hasError = true;
    });

    builder.addCase(getPronoStatsThunk.fulfilled, (state, action) => {
      state.selected.stats = action.payload;
    });

    builder.addCase(fetchPronoParticipantsThunk.pending, state => {
      state.selected.isParticipantsLoading = true;
    });
    builder.addCase(fetchPronoParticipantsThunk.fulfilled, (state, action) => {
      state.selected.participants = action.payload.participants;
      state.selected.totalParticipants = action.payload.total;
      state.selected.isParticipantsLoading = false;
    });
    builder.addCase(fetchPronoParticipantsThunk.rejected, state => {
      state.selected.isParticipantsLoading = false;
    });

    builder.addCase(getPronoDrawThunk.fulfilled, (state, action) => {
      state.selected.draw = action.payload;
    });
    builder.addCase(getPronoDrawThunk.rejected, state => {
      state.selected.draw = undefined;
    });

    builder.addCase(runPronoDrawThunk.pending, state => {
      state.selected.isDrawing = true;
    });
    builder.addCase(runPronoDrawThunk.fulfilled, (state, action) => {
      state.selected.draw = action.payload;
      state.selected.isDrawing = false;
      if (state.selected.gameInfo) {
        state.selected.gameInfo.status = PronoStatus.DRAWN;
      }
    });
    builder.addCase(runPronoDrawThunk.rejected, state => {
      state.selected.isDrawing = false;
    });

    builder.addCase(updatePronoGameThunk.fulfilled, (state, action) => {
      state.selected.gameInfo = action.payload;
    });

    builder.addCase(setPronoParticipationThunk.fulfilled, (state, action) => {
      state.selected.gameInfo = action.payload;
    });

    builder.addCase(deletePronoThunk.fulfilled, (state, action) => {
      pronoGamesAdapter.removeOne(state.list, action.payload);
      state.list.total = Math.max(0, state.list.total - 1);
    });
  },
});

export const { updateCurrentProno, resetCurrentForm, setValidForm } = actions;
